import api from "../../utils/api";
import { debounce } from "../../utils/debounce";
import { localStorageNameCompleted, triggerAttributeName } from "./constants";
import { getAutoStartEnabled, isDefined } from "./utils";
import { logger } from "../../utils/console";
export class GuidedTour {
    constructor() {
        this.data = null;
        this.dataScriptSelector = undefined;
        this.endpoint = undefined;
        this.autoStart = false;
        this.staticUsage = false;
        this.persist = true;
        this.isActive = false;
        this.viewportChanging = false;
        this.currentStep = null;
    }
    el;
    targetRect = null;
    overlayElement = null;
    currentStepIndex = -1;
    viewportFixed = false;
    get totalSteps() {
        if (isDefined(this.data) && Array.isArray(this.data?.steps)) {
            return this.data.steps.length;
        }
        return 0;
    }
    setData() {
        if (this.data === null) {
            this.loadData();
        }
        else {
            this.tourHash = generateHash(JSON.stringify(this.data)).toString();
            this.hasCompletedTour = this.getHasCompletedTour();
            if ((this.data.autoStart || this.autoStart) && !this.hasCompletedTour && getAutoStartEnabled(this.tourHash)) {
                this.activate();
            }
        }
    }
    setEndpoint() {
        if (typeof this.endpoint === "string") {
            this.loadData();
        }
    }
    setIsActive() {
        document.removeEventListener("click", this.handleClickDoc);
        if (!this.isActive) {
            document.addEventListener("click", this.handleClickDoc);
        }
    }
    setViewportChanging() {
        if (this.viewportChanging) {
            if (this.overlayElement !== null) {
                this.overlayElement.targetRect = null;
            }
        }
    }
    setCurrentStep() {
        this.viewportFixed = false;
        if (this.currentStep !== null) {
            this.showCurrentStep();
        }
    }
    handleScroll() {
        this.viewportChanging = true;
        this.viewportFixed = false;
        if (isDefined(this.overlayElement)) {
            if (this.overlayElement !== null) {
                this.overlayElement.step = null;
            }
        }
        this.showCurrentStepDebounced();
    }
    handleResize() {
        this.viewportChanging = true;
        if (isDefined(this.overlayElement)) {
            if (this.overlayElement !== null) {
                this.overlayElement.step = null;
            }
        }
        this.showCurrentStepDebounced();
    }
    /** Fired when the current step has changed */
    uxGuidedTourChange;
    /**
     * Fired when the guided-tour is canceled
     * @event
     */
    uxGuidedTourCancel;
    /**
     * Fired when the guided-tour is finished
     * @event
     */
    uxGuidedTourFinish;
    /**
     * Fired when the guided-tour is started
     * @event
     */
    uxGuidedTourActivate;
    /**
     * Jump to next guided-tour step
     * @returns {Promise<void>}
     */
    async nextStep() {
        if (!this.isActive) {
            return;
        }
        if (isDefined(this.data?.outro) && this.currentStep === this.data?.outro) {
            this.deactivate();
            return;
        }
        this.showStep(this.currentStepIndex + 1);
    }
    /**
     * Jump to previous guided-tour step
     * @returns {Promise<void>}
     */
    async prevStep() {
        if (!this.isActive || this.currentStepIndex <= 0) {
            return;
        }
        this.showStep(this.currentStepIndex - 1);
    }
    /**
     * Activate the guided-tour
     * @returns {Promise<void>}
     */
    async activate() {
        if (this.isActive || !isDefined(this.data)) {
            return;
        }
        // ToDo: move logic to storybook asap
        // deactivate other guided tours (storybook)
        if (window.parent !== null) {
            window.parent.document.querySelectorAll("ux-guided-tour").forEach((tour) => {
                if (tour !== this.el) {
                    tour.deactivate();
                }
            });
        }
        document.querySelectorAll("ux-guided-tour").forEach((tour) => {
            if (tour !== this.el) {
                tour.deactivate();
            }
        });
        this.isActive = true;
        this.uxGuidedTourActivate.emit();
        if (isDefined(this.data?.intro)) {
            this.showIntro();
        }
        else {
            this.showStep(0);
        }
    }
    /**
     * Deactivate the guided-tour
     * @returns {Promise<void>}
     */
    async deactivate() {
        if (!this.isActive) {
            return;
        }
        if (isDefined(this.data?.outro) && this.currentStep !== this.data?.outro) {
            this.showOutro();
            return;
        }
        this.removeOverlay();
        this.isActive = false;
        this.currentStep = null;
        this.currentStepIndex = -1;
    }
    componentWillLoad() {
        this.setData();
    }
    disconnectedCallback() {
        document.removeEventListener("click", this.handleClickDoc);
        this.removeOverlay();
    }
    finish() {
        logger.info("Guided tour finished");
        this.setHasCompletedTour();
        this.uxGuidedTourFinish.emit();
    }
    onCancel() {
        logger.info("Guided tour canceled");
        this.uxGuidedTourCancel.emit();
    }
    loadData = async () => {
        if (this.data !== null && Array.isArray(this.data.steps)) {
            this.setIsActive();
            return;
        }
        if (typeof this.endpoint === "string") {
            try {
                this.data = await api(this.endpoint);
                this.data = this.staticUsage && this.data ? this.filterValidSteps(this.data) : this.data;
                this.setIsActive();
                return;
            }
            catch (e) {
                if (e instanceof Error) {
                    logger.warn(`Invalid guided-tour data received from endpoint ${e.message} `);
                }
            }
        }
        const scriptElement = document.querySelector(this.dataScriptSelector);
        if (scriptElement !== null) {
            try {
                this.data = JSON.parse(scriptElement.innerText);
                this.data = this.staticUsage && this.data ? this.filterValidSteps(this.data) : this.data;
                this.setIsActive();
            }
            catch (e) {
                if (e instanceof Error) {
                    logger.warn(`Invalid guided-tour data ${e.message} `);
                }
            }
        }
    };
    filterValidSteps(data) {
        const validSteps = data.steps.filter((step) => {
            return step.target !== undefined && document.querySelector(step.target) !== null;
        });
        return { ...data, steps: validSteps };
    }
    tourHash;
    completedTourHashes = [];
    hasCompletedTour = false;
    getHasCompletedTour = () => {
        if (!this.persist) {
            return false;
        }
        const val = window.localStorage.getItem(localStorageNameCompleted);
        if (typeof val !== "string" || val.length === 0) {
            return false;
        }
        try {
            const hashes = JSON.parse(val);
            if (Array.isArray(hashes)) {
                this.completedTourHashes = hashes;
                return this.completedTourHashes.includes(this.tourHash);
            }
        }
        catch {
            return false;
        }
        return false;
    };
    setHasCompletedTour = () => {
        if (!this.persist) {
            return;
        }
        if (typeof this.tourHash !== "string" || this.tourHash.length === 0 || this.completedTourHashes.includes(this.tourHash)) {
            return;
        }
        try {
            this.completedTourHashes.push(this.tourHash);
            window.localStorage.setItem(localStorageNameCompleted, JSON.stringify(this.completedTourHashes));
        }
        catch {
            return;
        }
    };
    removeOverlay = () => {
        if (this.overlayElement !== null) {
            this.overlayElement.remove();
            this.overlayElement = null;
        }
    };
    showStep = (index) => {
        if (!this.isActive) {
            return;
        }
        if (isDefined(this.data) && this.data?.steps[index] !== undefined) {
            if (this.currentStep !== this.data?.steps[index]) {
                this.uxGuidedTourChange.emit({
                    prevStep: this.currentStep || null,
                    nextStep: this.data?.steps[index] ?? { target: "", targetOffset: 0, title: "" }
                });
            }
            this.currentStepIndex = index;
            this.currentStep = this.data?.steps[index];
        }
        else {
            this.deactivate();
        }
    };
    showIntro = () => {
        if (!this.isActive) {
            return;
        }
        if (isDefined(this.data) && isDefined(this.data?.intro)) {
            if (this.currentStep !== this.data?.intro) {
                this.uxGuidedTourChange.emit({
                    prevStep: this.currentStep || null,
                    nextStep: this.data?.intro
                });
            }
            this.currentStepIndex = -1;
            if (this.data?.intro) {
                this.currentStep = this.data.intro;
            }
        }
    };
    showOutro = () => {
        if (!this.isActive) {
            return;
        }
        if (isDefined(this.data) && isDefined(this.data?.outro)) {
            if (this.currentStep !== this.data?.outro) {
                this.uxGuidedTourChange.emit({
                    prevStep: this.currentStep || null,
                    nextStep: this.data?.outro
                });
            }
            this.currentStepIndex = -1;
            if (this.data?.outro) {
                this.currentStep = this.data.outro;
            }
        }
    };
    scrollToTarget = () => {
        const { viewportFixed, targetRect } = this;
        if (targetRect === null || viewportFixed) {
            return true;
        }
        const verticalCenter = this.targetRect ? Math.round(this.targetRect.top + 0.5 * this.targetRect.height) : 0;
        let scrollOffset = 0;
        if (targetRect.top < 0) {
            scrollOffset = verticalCenter - 0.5 * window.innerHeight;
        }
        else if (targetRect.top + targetRect.height > window.innerHeight) {
            scrollOffset = verticalCenter + 0.5 * window.innerHeight;
        }
        if (scrollOffset === 0) {
            this.viewportFixed = true;
            return true;
        }
        window.scrollBy(0, scrollOffset);
        this.viewportFixed = true;
        return false;
    };
    showCurrentStep = () => {
        const { currentStep, viewportChanging } = this;
        if (currentStep === null || viewportChanging) {
            this.targetRect = null;
            return;
        }
        const el = typeof currentStep.target === "string" ? document.querySelector(currentStep.target) : null;
        this.targetRect = el !== null ? el.getBoundingClientRect() : null;
        if (!this.scrollToTarget()) {
            this.showCurrentStepDebounced();
            return;
        }
        if (this.overlayElement === null) {
            const overlay = document.createElement("ux-guided-tour-overlay");
            overlay.addEventListener("click", this.handleClickOverlay);
            document.body.appendChild(overlay);
            this.overlayElement = overlay;
        }
        this.overlayElement.tourHash = this.tourHash;
        this.overlayElement.targetRect = this.targetRect;
        if (this.currentStep !== null) {
            this.overlayElement.step = this.currentStep;
        }
        this.overlayElement.stepIndex = this.currentStepIndex;
        this.overlayElement.totalSteps = this.totalSteps;
        if (this.overlayElement !== null) {
            this.overlayElement.autoStartSwitch = (this.data?.autoStart ?? false) && this.currentStep === this.data?.intro;
        }
        this.overlayElement.autoStartLabel = this.data?.autoStartLabel ?? "";
        this.overlayElement.finishTourButtonLabel = this.data?.finishTourButtonLabel;
    };
    showCurrentStepDebounced = debounce(() => {
        this.viewportChanging = false;
        if (this.currentStep !== null) {
            this.showCurrentStep();
        }
    }, 100);
    /** Handle document click events that should activate the guided-tour */
    handleClickDoc = (event) => {
        const triggerElement = event.target.closest(`[${triggerAttributeName}=start]`);
        if (triggerElement) {
            this.activate();
        }
    };
    /** Handle overlay click events that should control the guided-tour */
    handleClickOverlay = (event) => {
        const triggerElement = event.target.closest(`[${triggerAttributeName}]`);
        if (triggerElement === null) {
            return;
        }
        const action = triggerElement.getAttribute(triggerAttributeName) || "";
        switch (action) {
            case "next":
                this.nextStep();
                break;
            case "prev":
                this.prevStep();
                break;
            case "stop":
                this.deactivate();
                this.onCancel();
                break;
            case "finish":
                this.deactivate();
                this.finish();
                break;
            default:
                break;
        }
    };
    static get is() { return "ux-guided-tour"; }
    static get originalStyleUrls() {
        return {
            "$": ["guided-tour.scss"]
        };
    }
    static get styleUrls() {
        return {
            "$": ["guided-tour.css"]
        };
    }
    static get properties() {
        return {
            "data": {
                "type": "unknown",
                "mutable": false,
                "complexType": {
                    "original": "GuidedTourData | null",
                    "resolved": "GuidedTourData | null",
                    "references": {
                        "GuidedTourData": {
                            "location": "import",
                            "path": "./types",
                            "id": "src/components/guided-tour/types.ts::GuidedTourData"
                        }
                    }
                },
                "required": false,
                "optional": false,
                "docs": {
                    "tags": [],
                    "text": "The storyboard configuration"
                },
                "defaultValue": "null"
            },
            "dataScriptSelector": {
                "type": "string",
                "mutable": false,
                "complexType": {
                    "original": "string",
                    "resolved": "string",
                    "references": {}
                },
                "required": false,
                "optional": false,
                "docs": {
                    "tags": [],
                    "text": "CSS selector used to find the data script tag in the DOM tree"
                },
                "attribute": "data-script-selector",
                "reflect": false
            },
            "endpoint": {
                "type": "string",
                "mutable": false,
                "complexType": {
                    "original": "string",
                    "resolved": "string",
                    "references": {}
                },
                "required": false,
                "optional": false,
                "docs": {
                    "tags": [],
                    "text": "API endpoint url from which data is requested"
                },
                "attribute": "endpoint",
                "reflect": false
            },
            "autoStart": {
                "type": "boolean",
                "mutable": false,
                "complexType": {
                    "original": "boolean",
                    "resolved": "boolean",
                    "references": {}
                },
                "required": false,
                "optional": false,
                "docs": {
                    "tags": [],
                    "text": "Manage autostart additionally to autostart via data"
                },
                "attribute": "auto-start",
                "reflect": false,
                "defaultValue": "false"
            },
            "staticUsage": {
                "type": "boolean",
                "mutable": false,
                "complexType": {
                    "original": "boolean",
                    "resolved": "boolean",
                    "references": {}
                },
                "required": false,
                "optional": false,
                "docs": {
                    "tags": [],
                    "text": "Indicates if the component is used in a static context"
                },
                "attribute": "static-usage",
                "reflect": false,
                "defaultValue": "false"
            },
            "persist": {
                "type": "boolean",
                "mutable": false,
                "complexType": {
                    "original": "boolean",
                    "resolved": "boolean",
                    "references": {}
                },
                "required": false,
                "optional": false,
                "docs": {
                    "tags": [],
                    "text": "Indicates if the tour state should be persisted using localStorage. Defaults to true."
                },
                "attribute": "persist",
                "reflect": false,
                "defaultValue": "true"
            }
        };
    }
    static get states() {
        return {
            "isActive": {},
            "viewportChanging": {},
            "currentStep": {}
        };
    }
    static get events() {
        return [{
                "method": "uxGuidedTourChange",
                "name": "uxGuidedTourChange",
                "bubbles": true,
                "cancelable": true,
                "composed": true,
                "docs": {
                    "tags": [],
                    "text": "Fired when the current step has changed"
                },
                "complexType": {
                    "original": "{\n        prevStep: {\n            target?: string;\n            targetOffset?: number;\n            title?: string;\n        } | null;\n        nextStep: {\n            target?: string;\n            targetOffset?: number;\n            title?: string;\n        };\n    }",
                    "resolved": "{ prevStep: { target?: string | undefined; targetOffset?: number | undefined; title?: string | undefined; } | null; nextStep: { target?: string | undefined; targetOffset?: number | undefined; title?: string | undefined; }; }",
                    "references": {}
                }
            }, {
                "method": "uxGuidedTourCancel",
                "name": "uxGuidedTourCancel",
                "bubbles": true,
                "cancelable": true,
                "composed": true,
                "docs": {
                    "tags": [{
                            "name": "event",
                            "text": undefined
                        }],
                    "text": "Fired when the guided-tour is canceled"
                },
                "complexType": {
                    "original": "void",
                    "resolved": "void",
                    "references": {}
                }
            }, {
                "method": "uxGuidedTourFinish",
                "name": "uxGuidedTourFinish",
                "bubbles": true,
                "cancelable": true,
                "composed": true,
                "docs": {
                    "tags": [{
                            "name": "event",
                            "text": undefined
                        }],
                    "text": "Fired when the guided-tour is finished"
                },
                "complexType": {
                    "original": "void",
                    "resolved": "void",
                    "references": {}
                }
            }, {
                "method": "uxGuidedTourActivate",
                "name": "uxGuidedTourActivate",
                "bubbles": true,
                "cancelable": true,
                "composed": true,
                "docs": {
                    "tags": [{
                            "name": "event",
                            "text": undefined
                        }],
                    "text": "Fired when the guided-tour is started"
                },
                "complexType": {
                    "original": "void",
                    "resolved": "void",
                    "references": {}
                }
            }];
    }
    static get methods() {
        return {
            "nextStep": {
                "complexType": {
                    "signature": "() => Promise<void>",
                    "parameters": [],
                    "references": {
                        "Promise": {
                            "location": "global",
                            "id": "global::Promise"
                        }
                    },
                    "return": "Promise<void>"
                },
                "docs": {
                    "text": "Jump to next guided-tour step",
                    "tags": [{
                            "name": "returns",
                            "text": undefined
                        }]
                }
            },
            "prevStep": {
                "complexType": {
                    "signature": "() => Promise<void>",
                    "parameters": [],
                    "references": {
                        "Promise": {
                            "location": "global",
                            "id": "global::Promise"
                        }
                    },
                    "return": "Promise<void>"
                },
                "docs": {
                    "text": "Jump to previous guided-tour step",
                    "tags": [{
                            "name": "returns",
                            "text": undefined
                        }]
                }
            },
            "activate": {
                "complexType": {
                    "signature": "() => Promise<void>",
                    "parameters": [],
                    "references": {
                        "Promise": {
                            "location": "global",
                            "id": "global::Promise"
                        },
                        "HTMLUxGuidedTourElement": {
                            "location": "global",
                            "id": "global::HTMLUxGuidedTourElement"
                        }
                    },
                    "return": "Promise<void>"
                },
                "docs": {
                    "text": "Activate the guided-tour",
                    "tags": [{
                            "name": "returns",
                            "text": undefined
                        }]
                }
            },
            "deactivate": {
                "complexType": {
                    "signature": "() => Promise<void>",
                    "parameters": [],
                    "references": {
                        "Promise": {
                            "location": "global",
                            "id": "global::Promise"
                        }
                    },
                    "return": "Promise<void>"
                },
                "docs": {
                    "text": "Deactivate the guided-tour",
                    "tags": [{
                            "name": "returns",
                            "text": undefined
                        }]
                }
            }
        };
    }
    static get elementRef() { return "el"; }
    static get watchers() {
        return [{
                "propName": "data",
                "methodName": "setData"
            }, {
                "propName": "endpoint",
                "methodName": "setEndpoint"
            }, {
                "propName": "isActive",
                "methodName": "setIsActive"
            }, {
                "propName": "viewportChanging",
                "methodName": "setViewportChanging"
            }, {
                "propName": "currentStep",
                "methodName": "setCurrentStep"
            }];
    }
    static get listeners() {
        return [{
                "name": "scroll",
                "method": "handleScroll",
                "target": "window",
                "capture": false,
                "passive": true
            }, {
                "name": "resize",
                "method": "handleResize",
                "target": "window",
                "capture": false,
                "passive": true
            }];
    }
}
const generateHash = (s) => s.split("").reduce((a, b) => {
    // eslint-disable-next-line no-bitwise
    a = (a << 5) - a + b.charCodeAt(0);
    // eslint-disable-next-line no-bitwise
    return a & a;
}, 0);
//# sourceMappingURL=guided-tour.js.map
